
import React, { useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { Region } from '../../../data/types/region-types';
import { FeatureCollection } from 'geojson';
import MapLayers from './MapLayers';
import UnreachedPins from '../markers/UnreachedPins';
import { City } from '../../../data/types/city-types';
import CityMarkers from '../markers/CityMarkers';
import { getCitiesByRegion } from '../../../utils/city-utils';

interface RegionPolygonsProps {
  map: mapboxgl.Map;
  geoJsonData: FeatureCollection;
  propertyKey: string;
  regions: Region[];
  selectedRegion: Region | null;
  onRegionClick: (region: Region) => void;
  mapZoomLevel?: 'country' | 'region' | 'city';
}

const RegionPolygons: React.FC<RegionPolygonsProps> = ({
  map,
  geoJsonData,
  propertyKey,
  regions,
  selectedRegion,
  onRegionClick,
  mapZoomLevel,
}) => {
  const [regionCities, setRegionCities] = useState<City[]>([]);
  const [cityMarkers, setCityMarkers] = useState<mapboxgl.Marker[]>([]);
  
  // Get cities for the region when a region is selected and we're at region zoom level
  useEffect(() => {
    if (selectedRegion && (mapZoomLevel === 'region' || mapZoomLevel === 'city')) {
      console.log(`Loading cities for ${selectedRegion.english_name}`);
      const cities = getCitiesByRegion(selectedRegion.region_id);
      setRegionCities(cities);
    } else {
      setRegionCities([]);
    }
    
    // Clean up markers when component unmounts
    return () => {
      cityMarkers.forEach(marker => marker.remove());
      setCityMarkers([]);
    };
  }, [selectedRegion, mapZoomLevel]);

  const handleCityClick = (city: City) => {
    console.log("City clicked:", city.name);
    // You can implement additional functionality here if needed
  };

  return (
    <>
      <MapLayers 
        map={map}
        geoJsonData={geoJsonData}
        propertyKey={propertyKey}
        regions={regions}
        selectedRegion={selectedRegion}
      />
      
      {/* Show unreached pins when we're zoomed into a region */}
      {selectedRegion && mapZoomLevel === 'region' && (
        <UnreachedPins 
          map={map} 
          region={selectedRegion}
        />
      )}
      
      {/* Show city markers when we're zoomed into a region or city */}
      {selectedRegion && regionCities.length > 0 && (mapZoomLevel === 'region' || mapZoomLevel === 'city') && (
        <CityMarkers
          map={map}
          cities={regionCities}
          onCityClick={handleCityClick}
          markerRefs={cityMarkers}
          selectedCity={null}
          selectedRegion={selectedRegion}
        />
      )}
    </>
  );
};

// Provide proper default props
RegionPolygons.defaultProps = {
  geoJsonData: { type: 'FeatureCollection', features: [] },
  propertyKey: 'region_id',
  mapZoomLevel: 'country'
};

export default RegionPolygons;
